import React, { useCallback, useState, useRef, useLayoutEffect } from 'react';
import { View, ScrollView } from 'react-native';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { IconHome, IconProfile, IconWork } from 'design-system/icons';
import { Button, SettingsSection, SettingsItemInput, withAnimatedNavigationHeader } from 'design-system';
import Sentry from 'utils/sentry';
import useColors from 'hooks/useColors';
import { useAppDispatch } from 'store/hooks';
import { OnboardingFlow } from 'utils/types/navigation';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useCommonInvestStyles } from './styles';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
const strings = {
    next: 'Continue',
    jobTitle: 'Job title',
    employerName: 'Name of employer',
    employerAddress: 'Employer address',
    alertTitle: 'Hold on 💫',
    placeholder: '(Optional)',
    alertMissingJobTitle: 'You need to specify your job title.',
    alertMissingEmployerName: 'You need to specify employer name.',
    ok: 'OK',
};
const EmployerDetailsScreen = withAnimatedNavigationHeader(({ route: { params }, navigation: { popToTop, setOptions } }) => {
    const colors = useColors();
    const commonStyles = useCommonInvestStyles();
    const navigateToNextScreen = useNavigateToNextScreen();
    const navigateToNextScreenForPots = useNavigateToNextScreenForPotsOnboarding();
    useLayoutEffect(() => {
        setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={params?.flow === OnboardingFlow.Pots ? HELP_ARTICLES_KEY.POTS_ONBOARDING : undefined}/>),
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, params?.flow, setOptions]);
    useAmplitudeOnMountEvent(params?.flow, 'User Employer Details Screen');
    const [jobTitle, setJobTitle] = useState('');
    const [employerName, setEmployerName] = useState('');
    const [employerAddress, setEmployerAddress] = useState('');
    const employerNameRef = useRef(null);
    const employerAddressRef = useRef(null);
    const jobTitleRef = useRef(null);
    const dispatch = useAppDispatch();
    const handleNext = useCallback(() => {
        if (!params)
            return;
        try {
            jobTitleRef.current?.blur();
            employerNameRef.current?.blur();
            employerAddressRef.current?.blur();
            const { annualIncomeMin, annualIncomeMax, liquidNetWorthMin, liquidNetWorthMax, fundingSource, employmentStatus, } = params || {};
            if (annualIncomeMin !== undefined &&
                annualIncomeMin !== null &&
                annualIncomeMax !== undefined &&
                annualIncomeMax !== null &&
                liquidNetWorthMin !== undefined &&
                liquidNetWorthMin !== null &&
                liquidNetWorthMax !== undefined &&
                liquidNetWorthMax !== null &&
                !!fundingSource) {
                const newParams = {
                    annualIncomeMin,
                    annualIncomeMax,
                    liquidNetWorthMin,
                    liquidNetWorthMax,
                    fundingSource,
                    employmentStatus,
                    jobTitle,
                    employerName,
                    employerAddress,
                };
                dispatch({
                    params: newParams,
                    type: 'SAVE_ONBOARDING_STATE',
                    screen: 'InvestBrokerRequirements',
                });
                if (params?.flow === OnboardingFlow.Pots || params?.flow === OnboardingFlow.Wallet) {
                    navigateToNextScreenForPots();
                }
                else {
                    navigateToNextScreen();
                }
            }
            else {
                Sentry.logError({
                    filename: 'InvestEmployerDetails',
                    message: 'User not able to go ahead',
                });
                popToTop();
            }
        }
        catch {
            // Handled in middleware
        }
    }, [employerAddress, employerName, jobTitle, navigateToNextScreen, navigateToNextScreenForPots, params]);
    const onSubmit = useCallback(async () => {
        handleNext();
    }, [handleNext]);
    const onFocusEmployerAddress = useCallback(() => {
        if (!employerAddress) {
            employerAddressRef.current?.focus();
        }
        else if (!jobTitle) {
            jobTitleRef.current?.focus();
        }
        else {
            employerNameRef.current?.blur();
            onSubmit();
        }
    }, [employerAddress, jobTitle, onSubmit]);
    const onFocusJobTitle = useCallback(() => {
        if (!jobTitle) {
            jobTitleRef.current?.focus();
        }
        else if (!employerName) {
            employerNameRef.current?.focus();
        }
        else {
            employerAddressRef.current?.blur();
            onSubmit();
        }
    }, [jobTitle, employerName, onSubmit]);
    return (<View style={commonStyles.containerNoPadding}>
        <StatusBar barStyle="dark-content" animated/>
        <ScrollView scrollEnabled={false} keyboardShouldPersistTaps="handled" contentContainerStyle={commonStyles.contentContainer}>
          <ScreenInfoHeader title="What are your employer details?"/>
          <SettingsSection noMarginTop>
            <SettingsItemInput placeholder={strings.placeholder} maxLength={100} ref={employerNameRef} icon={<IconProfile gradientKey="skyBlue"/>} value={employerName} onChangeText={setEmployerName} onClearTextInput={() => {
            setEmployerName('');
        }} description={strings.employerName} returnKeyType="next" blurOnSubmit={false} onSubmitEditing={onFocusEmployerAddress} autoCapitalize="words"/>
            <SettingsItemInput placeholder={strings.placeholder} maxLength={255} ref={employerAddressRef} icon={<IconHome gradientKey="orange"/>} value={employerAddress} onChangeText={setEmployerAddress} onClearTextInput={() => {
            setEmployerAddress('');
        }} description={strings.employerAddress} returnKeyType="next" blurOnSubmit={false} onSubmitEditing={onFocusJobTitle} autoCapitalize="words"/>
            <SettingsItemInput placeholder={strings.placeholder} ref={jobTitleRef} maxLength={50} icon={<IconWork gradientColors={colors.gradients.lightPink.colors}/>} value={jobTitle} onChangeText={setJobTitle} description={strings.jobTitle} returnKeyType="done" onSubmitEditing={onSubmit} autoCapitalize="words"/>
          </SettingsSection>
        </ScrollView>
        <Button brand style={commonStyles.button} title={strings.next} onPress={onSubmit}/>
      </View>);
}, {});
export default EmployerDetailsScreen;
